import { toast } from 'react-toastify';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { ButtonType } from '../../types/button-type';
import { generatePath } from 'react-router-dom';
import ROUTE from '../../constants/route-constants';
import { connect } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';

// Store
import { Dispatch, RootState } from '../../store/store';
import { CycleComponents, DataZoomEvent } from '../CycleChartPreview/types';
import { getLttbData } from '../CycleChartPreview/helpers';
import { ITakeGraphScreenshotProps, ITakeMainScreenshotProps } from '../../store/models/reports';
import { useTranslation } from 'react-i18next';
import { IExcludedChannelNames } from '../ReportChannelsModal/ReportChannelsModal';
import { CycleController } from '../../store/models/controllers';
import { SET_POINT_NAME } from '../CycleChartPreview/constants';
import { CycleReports } from '../../store/models/cycles';

import { appConfig } from '../../configs/app-config';

interface IGraphScreenshotProps {
  loading: boolean;
  mainScreenLoading: boolean;
  cycleId: string;
  clientId: string;
  contractId: string;
  screenId: string | undefined;
  chartRef: React.MutableRefObject<ReactECharts | null>;
  cycleComponents: CycleComponents;
  excludedChannelNames: IExcludedChannelNames[];
  cycleControllers: CycleController[];
  cycleReports: CycleReports[];
  takeGraphScreenshot: (data: ITakeGraphScreenshotProps) => Promise<void>;
  takeMainGraphScreenshot: (data: ITakeMainScreenshotProps) => Promise<void>;
}

const GraphScreenshot = (data: IGraphScreenshotProps) => {
  const {
    loading,
    mainScreenLoading,
    cycleId,
    chartRef,
    cycleComponents,
    excludedChannelNames,
    cycleControllers,
    takeGraphScreenshot,
    takeMainGraphScreenshot,
    clientId,
    contractId,
    screenId,
    cycleReports,
  } = data;

  const isShowCycleReportButton = appConfig.apiVersion === '2';

  const channelsToHide = excludedChannelNames.flatMap((channelNames) => {
    const controllerName =
      cycleControllers.find((item) => item.controller.id === channelNames.controllerId)?.controller.name || null;
    if (!controllerName) return [];

    const channelList = [...channelNames.excludeChannels, ...channelNames.excludeThermocouples];
    return channelList.map((channel) => `${controllerName}#${channel}`);
  });

  const pathToCycleReport = screenId
    ? generatePath(ROUTE.CYCLE_REPORT, { id: cycleId, clientId, contractId, screenId })
    : generatePath(ROUTE.CYCLE_REPORT_WITHOUT_SCREEN, { id: cycleId, clientId, contractId });

  const cycleReportId = cycleReports?.find((report) => report.isActive).id;

  const pathToCycleReportPage = screenId
    ? generatePath(ROUTE.CYCLE_REPORTS, { id: cycleId, cycleReportId, clientId, contractId, screenId })
    : generatePath(ROUTE.CYCLE_REPORTS_WITHOUT_SCREEN, { id: cycleId, cycleReportId, clientId, contractId });

  const takeScreenshot = async (options: any, coordRange?: (string | number | Date)[][]) => {
    const filteredDataZoom = options.dataZoom.filter((item) => item.type !== 'slider');
    const filteredOptions = { ...options, dataZoom: filteredDataZoom };

    filteredOptions.series = filteredOptions.series.filter(
      (item) => !channelsToHide.includes(item.name) && item.id !== SET_POINT_NAME,
    );

    const chartContainer = document.createElement('div');
    chartContainer.style.width = '1920px';
    chartContainer.style.height = '1080px';

    const tempChart = echarts.init(chartContainer);

    tempChart.setOption(filteredOptions);

    return new Promise<Blob>((resolve) => {
      if (coordRange) {
        const xStart = coordRange[0][0];
        const xEnd = coordRange[0][1];
        const yStart = coordRange[1][0];
        const yEnd = coordRange[1][1];

        const chartWidth = 1920;
        const chartHeight = 1080;

        const grid = tempChart.getOption().grid[0];

        const gridLeft = parseInt(grid.left);
        const gridRight = parseInt(grid.right);
        const gridTop = parseInt(grid.top);
        const gridBottom = parseInt(grid.bottom);

        const gridWidth = chartWidth - gridLeft - gridRight;
        const gridHeight = chartHeight - gridTop - gridBottom;

        const xStartPixel = (Number(xStart) / 100) * gridWidth + gridLeft;
        const xEndPixel = (Number(xEnd) / 100) * gridWidth + gridLeft;
        const yStartPixel = (Number(yStart) / 100) * gridHeight + gridBottom;
        const yEndPixel = (Number(yEnd) / 100) * gridHeight + gridBottom;

        const topLeft = [xStartPixel, 1080 - yEndPixel];
        const topRight = [xEndPixel, 1080 - yEndPixel];
        const bottomLeft = [xStartPixel, 1080 - yStartPixel];
        const bottomRight = [xEndPixel, 1080 - yStartPixel];

        tempChart.dispatchAction({
          type: 'brush',
          areas: [
            {
              brushType: 'polygon',
              range: [topLeft, bottomLeft, bottomRight, topRight],
            },
          ],
        });
      }
      tempChart.resize();
      tempChart.on('finished', () => {
        const base64 = tempChart.getDataURL({ type: 'png' });
        const byteCharacters = atob(base64.split(',')[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        resolve(new Blob([byteArray], { type: 'image/png' }));
      });
    });
  };

  const handleScreenshot = async () => {
    try {
      const eChartInstance = chartRef.current?.getEchartsInstance();
      if (!eChartInstance) return;

      const channels = cycleComponents.deviceSeriesData.map((device) => device.name.split('#').at(1));
      const originalOptions = eChartInstance.getOption();

      const xAxisArray = Array.isArray(originalOptions.xAxis) ? originalOptions.xAxis : [originalOptions.xAxis];
      originalOptions.xAxis = xAxisArray.map((axis) => ({
        ...axis,
        axisLabel: {
          ...axis.axisLabel,
          fontSize: 20,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#999999',
          },
        },
      }));

      const yAxisArray = Array.isArray(originalOptions.yAxis) ? originalOptions.yAxis : [originalOptions.yAxis];
      originalOptions.yAxis = yAxisArray.map((yAxis) => ({
        ...yAxis,
        axisLabel: {
          ...yAxis.axisLabel,
          fontSize: 20,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#999999',
          },
        },
      }));

      const [yData, xData] = originalOptions.dataZoom ?? [
        { start: 0, end: 100 },
        { start: 0, end: 100 },
      ];

      const zoomedScreenshot = await takeScreenshot(originalOptions);
      let previewScreenshot = undefined;

      if (yData.start !== 0 || yData.end !== 100 || xData.start !== 0 || xData.end !== 100) {
        const coordRange = [
          [xData.start, xData.end],
          [yData.start, yData.end],
          [xData.startValue || null, xData.endValue || null],
        ];

        const newVisibleData = getLttbData(cycleComponents.source.length, cycleComponents.source);
        const xAxis = originalOptions.xAxis.map(item => {
          const { min, max, ...xAxisWithoutMinMax } = item
          return xAxisWithoutMinMax
        })
        const optionsWithoutZoom = {
          ...originalOptions,
          xAxis: xAxis,
          dataZoom: [
            {
              ...originalOptions.dataZoom.at(0),
              end: 100,
              start: 0,
            },
            {
              ...originalOptions.dataZoom.at(1),
              end: 100,
              start: 0,
            },
          ],
          series: originalOptions.series.map((seriesItem) => ({
            ...seriesItem,
            data: newVisibleData.map((d) => [d.time, d[seriesItem.id]]),
          })),
          brush: {
            toolbox: ['rect', 'clear'],
            brushType: 'rect',
            brushMode: 'single',
            xAxisIndex: 'all',
            transformable: true,
            brushStyle: {
              borderWidth: 4,
              color: 'transparent',
              borderColor: 'red',
            },
          },
        };

        previewScreenshot = await takeScreenshot(optionsWithoutZoom, coordRange);
      }

      //unused value
      const dataZoomEvent: DataZoomEvent = {
        batch: [],
        type: '',
      };

      await takeGraphScreenshot({
        cycleId,
        dataZoom: dataZoomEvent,
        screenshot: zoomedScreenshot,
        previewScreenshot,
        timeLine: { from: Number(xData.startValue), to: Number(xData.endValue) },
        channels,
      });

      toast.success('Graph screenshot request sent successfully');
    } catch (error) {
      toast.error('Failed to send screenshot request');
      console.error(error);
    }
  };

  const handleMainScreenshot = async () => {
    const eChartInstance = chartRef.current?.getEchartsInstance();
    if (!eChartInstance) return;

    const originalOptions = eChartInstance.getOption();

    const xAxisArray = Array.isArray(originalOptions.xAxis) ? originalOptions.xAxis : [originalOptions.xAxis];
    originalOptions.xAxis = xAxisArray.map((axis) => ({
      ...axis,
      axisLabel: {
        ...axis.axisLabel,
        fontSize: 20,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#999999',
        },
      },
    }));

    const yAxisArray = Array.isArray(originalOptions.yAxis) ? originalOptions.yAxis : [originalOptions.yAxis];
    originalOptions.yAxis = yAxisArray.map((yAxis) => ({
      ...yAxis,
      axisLabel: {
        ...yAxis.axisLabel,
        fontSize: 20,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: '#999999',
        },
      },
    }));

    const newVisibleData = getLttbData(cycleComponents.source.length, cycleComponents.source);

    const optionsWithoutZoom = {
      ...originalOptions,
      dataZoom: [
        {
          ...originalOptions.dataZoom.at(0),
          end: 100,
          start: 0,
        },
        {
          ...originalOptions.dataZoom.at(1),
          end: 100,
          start: 0,
        },
      ],
      series: originalOptions.series.map((seriesItem) => ({
        ...seriesItem,
        data: newVisibleData.map((d) => [d.time, d[seriesItem.id]]),
      })),
    };

    const unZoomedScreenshot = await takeScreenshot(optionsWithoutZoom);
    await takeMainGraphScreenshot({
      cycleId,
      screenshot: unZoomedScreenshot,
    });
  };

  const { t } = useTranslation();

  const buttonClassnames = 'rounded-md bg-primary px-4 py-1.5 font-bold text-white hover:bg-[#ff9146]';
  const cycleComponentsExists = cycleComponents && cycleComponents.source.length > 0;

  return (
    <div className="c-graph-screenshot flex flex-col sm:items-center sm:flex-row gap-3">
      {cycleComponentsExists && (
        <>
          <InteractiveButton
            className={buttonClassnames}
            type={ButtonType.Primary}
            text={mainScreenLoading ? t('labels.processing') : t('labels.takeMainChartSnapshot')}
            onClick={handleMainScreenshot}
            disabled={mainScreenLoading}
            as="button"
          />
          <InteractiveButton
            className={buttonClassnames}
            type={ButtonType.Primary}
            text={loading ? t('labels.processing') : t('labels.takeZoomedInSnapshot')}
            onClick={handleScreenshot}
            disabled={loading}
            as="button"
          />
        </>
      )}
      {!isShowCycleReportButton && (
        <InteractiveButton
          className={buttonClassnames}
          type={ButtonType.Primary}
          iconLeft={null}
          text={t('labels.openReportPage')}
          onClick={() => { }}
          as="a"
          href={pathToCycleReport}
          target="_blank"
        />
      )}

      {isShowCycleReportButton && (
        <InteractiveButton
          className={buttonClassnames}
          type={ButtonType.Primary}
          iconLeft={null}
          text={t('labels.openCycleReportPage')}
          onClick={() => { }}
          as="a"
          href={pathToCycleReportPage}
          target="_blank"
        />
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  loading: state.reports.loading,
  mainScreenLoading: state.reports.mainScreenLoading,
});

const mapDispatch = (dispatch: Dispatch) => ({
  takeGraphScreenshot: dispatch.reports.takeGraphScreenshot,
  takeMainGraphScreenshot: dispatch.reports.takeMainGraphScreenshot,
});

export default connect(mapState, mapDispatch)(GraphScreenshot);
